import React, { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Plus, Upload } from "lucide-react";
import { fmtDate } from "../lib/datetime";

const STATUS_BADGE = {
  Requested: "badge-blue",
  "RAMS Approved": "badge-blue",
  "DRA Signed": "badge-blue",
  Erected: "badge-amber",
  Standing: "badge-green",
  Struck: "badge-slate",
};

const STATUSES = ["Requested", "RAMS Approved", "DRA Signed", "Erected", "Standing", "Struck"];

export default function Structures() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [areas, setAreas] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterArea, setFilterArea] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const canEdit = ["admin", "contracts_manager", "supervisor"].includes(user?.role);

  const load = () => {
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (filterArea) params.area_id = filterArea;
    api.get("/structures", { params }).then((r) => setItems(r.data));
  };

  useEffect(() => {
    api.get("/areas").then((r) => setAreas(r.data));
  }, []);

  useEffect(load, [filterStatus, filterArea]);

  return (
    <div data-testid="structures-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>STRUCTURE REGISTER</div>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>Temporary works · live register</h1>
          <div style={{ color: "#64748b", marginTop: 4 }}>Single source of truth — each structure logged once.</div>
        </div>
        {canEdit ? (
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="open-import" className="btn-ghost" onClick={() => setShowImport(true)}><Upload size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Bulk import (CSV)</button>
            <button data-testid="open-create" className="btn-primary" onClick={() => setShowCreate(true)}><Plus size={14} style={{ marginRight: 6, verticalAlign: -2 }} />New structure</button>
          </div>
        ) : null}
      </header>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <select data-testid="filter-status" className="input-base" style={{ maxWidth: 200 }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filter-area" className="input-base" style={{ maxWidth: 280 }} value={filterArea} onChange={(e) => setFilterArea(e.target.value)}>
          <option value="">All areas</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <div className="mono" style={{ alignSelf: "center", color: "#64748b" }}>{items.length} structures</div>
      </div>

      <div className="card" style={{ padding: 0 }} data-testid="structures-table-wrap">
        <table data-testid="structures-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Description</th>
              <th>Area</th>
              <th>Rate</th>
              <th>Qty</th>
              <th>Status</th>
              <th>Next due</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`structure-row-${s.id}`} style={{ cursor: "pointer" }} onClick={() => navigate(`/structures/${s.id}`)}>
                <td className="mono" style={{ color: "#475569" }}>{s.id.slice(0, 8)}</td>
                <td style={{ fontWeight: 600 }}>{s.description}</td>
                <td>{s.area_name}<div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>{s.zone_classification}</div></td>
                <td className="mono">{s.price_schedule_ref}</td>
                <td className="mono" style={{ textAlign: "right" }}>{s.quantity} {s.unit}</td>
                <td><span className={`badge ${STATUS_BADGE[s.status] || "badge-slate"}`}>{s.status}</span></td>
                <td className="mono">{s.next_inspection_due ? fmtDate(s.next_inspection_due) : "—"}</td>
                <td><span className="badge badge-slate">{s.source}</span></td>
              </tr>
            ))}
            {items.length === 0 ? <tr><td colSpan={8} style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>No structures match filters</td></tr> : null}
          </tbody>
        </table>
      </div>

      {showCreate ? <CreateStructure areas={areas} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} /> : null}
      {showImport ? <ImportCsv onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} /> : null}
    </div>
  );
}

function Modal({ children, onClose, testid, title }) {
  return (
    <div data-testid={testid} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }} onClick={onClose}>
      <div className="card" style={{ width: "100%", maxWidth: 540, padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0, fontSize: 22, fontWeight: 900 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function CreateStructure({ areas, onClose, onCreated }) {
  const [areaId, setAreaId] = useState(areas[0]?.id || "");
  const [ref, setRef] = useState("");
  const [desc, setDesc] = useState("");
  const [qty, setQty] = useState(1);
  const [unit, setUnit] = useState("Each");
  const [status, setStatus] = useState("Requested");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [priceList, setPriceList] = useState([]);

  React.useEffect(() => {
    api.get("/price-schedule").then((r) => setPriceList(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/structures", { area_id: areaId, price_schedule_ref: ref, description: desc, quantity: Number(qty), unit, status, source: "new" });
      onCreated();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const pickRate = (item) => {
    setRef(item.ref);
    setUnit(item.unit || "Each");
    // Pre-fill description if empty (the user can override) — saves typing
    if (!desc.trim()) setDesc(item.description || "");
  };

  return (
    <Modal onClose={onClose} testid="modal-create-structure" title="New structure">
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Area</label>
          <select data-testid="create-area" className="input-base" value={areaId} onChange={(e) => setAreaId(e.target.value)} required>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Rate (search by description, ref, or unit)</label>
          <RateCombobox priceList={priceList} value={ref} onPick={pickRate} />
        </div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Description</label>
          <input data-testid="create-desc" className="input-base" value={desc} onChange={(e) => setDesc(e.target.value)} required placeholder="e.g. Independent scaffold for fryer hood maintenance" />
          <div className="mono" style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>Pre-filled from the rate above — edit freely to add the specifics for this structure.</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Qty</label>
            <input data-testid="create-qty" className="input-base" type="number" min="0" step="0.1" value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Unit</label>
            <select data-testid="create-unit" className="input-base" value={unit} onChange={(e) => setUnit(e.target.value)}>
              <option>Each</option><option>Metre</option><option>m²</option><option>Day</option><option>Event</option>
            </select>
          </div>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Initial status</label>
          <select data-testid="create-status" className="input-base" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {err ? <div className="badge badge-red" style={{ padding: "8px 12px" }}>{String(err)}</div> : null}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button data-testid="create-submit" type="submit" className="btn-primary" disabled={busy || !ref}>{busy ? "Saving…" : "Create"}</button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Searchable rate combobox. Substring filter across ref + description + unit + notes — not prefix-only.
 * Keyboard: arrow keys to navigate, Enter to select, Esc to close.
 */
function RateCombobox({ priceList, value, onPick }) {
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [activeIdx, setActiveIdx] = React.useState(0);
  const boxRef = React.useRef(null);

  // Show the picked entry's full label when not actively typing
  const picked = priceList.find((p) => p.ref === value);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filtered = React.useMemo(() => {
    if (!q.trim()) return priceList;
    const needle = q.trim().toLowerCase();
    return priceList.filter((p) => {
      const hay = `${p.ref || ""} ${p.description || ""} ${p.unit || ""} ${p.notes || ""}`.toLowerCase();
      return hay.includes(needle);   // substring, NOT startsWith
    });
  }, [q, priceList]);

  React.useEffect(() => { setActiveIdx(0); }, [q, open]);

  const pick = (item) => {
    onPick(item);
    setQ("");
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && filtered[activeIdx]) { e.preventDefault(); pick(filtered[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }} data-testid="create-rate-combobox">
      <input
        data-testid="create-rate-search"
        className="input-base"
        value={open ? q : (picked ? `${picked.ref} — ${picked.description} (${picked.unit}${picked.unit_price ? " · £" + picked.unit_price.toFixed(2) : ""})` : q)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setQ(""); setOpen(true); }}
        onKeyDown={onKey}
        placeholder="Search rates — type any word, e.g. ‘tower’, ‘fryer’, ‘monoflex’, ‘1C’…"
        autoComplete="off"
      />
      {open ? (
        <div data-testid="create-rate-dropdown" style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, background: "white", border: "1px solid #cbd5e1", borderRadius: 2, maxHeight: 280, overflowY: "auto", zIndex: 200, boxShadow: "0 8px 24px rgba(15,23,42,0.18)" }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 14, color: "#94a3b8", fontSize: 13, textAlign: "center" }}>No rates match “{q}”</div>
          ) : (
            filtered.map((p, i) => (
              <div
                key={p.ref}
                data-testid={`create-rate-option-${p.ref}`}
                onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                onMouseEnter={() => setActiveIdx(i)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  background: i === activeIdx ? "#f1f5f9" : "white",
                  borderBottom: "1px solid #f1f5f9",
                  display: "grid",
                  gridTemplateColumns: "56px 1fr auto",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <span className="mono" style={{ fontWeight: 700, color: "var(--brand-accent, #F05A14)" }}>{p.ref}</span>
                <span>
                  <div style={{ fontWeight: 600 }}>{p.description}</div>
                  {p.notes ? <div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>{p.notes}</div> : null}
                </span>
                <span className="mono" style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>
                  {p.unit}{p.unit_price ? ` · £${p.unit_price.toFixed(2)}` : ""}
                </span>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function ImportCsv({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/structures/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <Modal onClose={onClose} testid="modal-import-csv" title="Bulk import — day-one structures">
      <div className="mono" style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
        CSV columns: <b>area_name, price_schedule_ref, description, quantity, unit, status, source</b><br />
        Areas must match seeded names. Rows with status=Standing get an auto-logged initial inspection.
      </div>
      <a data-testid="download-template" href={`${API}/structures/bulk-import/template.csv`} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#ff5a00", fontWeight: 700, marginBottom: 12 }}>
        ↓ Download CSV template (with 2 example rows)
      </a>
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <input data-testid="import-file" className="input-base" type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        {err ? <div className="badge badge-red" style={{ padding: "8px 12px" }}>{String(err)}</div> : null}
        {result ? (
          <div data-testid="import-result" className="card" style={{ padding: 12, background: "#f8fafc" }}>
            <div style={{ fontWeight: 700 }}>Created: {result.created}</div>
            {result.errors?.length ? (
              <div style={{ marginTop: 6, color: "#b91c1c" }}>
                Errors ({result.errors.length}):
                <ul style={{ margin: "4px 0 0 16px", fontSize: 12 }}>
                  {result.errors.slice(0, 5).map((er, i) => <li key={i}>Row {er.row}: {er.error}</li>)}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn-ghost" onClick={result ? onDone : onClose}>{result ? "Done" : "Cancel"}</button>
          {!result ? <button data-testid="import-submit" type="submit" className="btn-primary" disabled={busy || !file}>{busy ? "Uploading…" : "Upload"}</button> : null}
        </div>
      </form>
    </Modal>
  );
}
